import { map } from "lodash-es";
import { useStyles } from "./Settings.style";
import { Icon } from "../../components/core/Icon/Icon";
import AppBar from "../../components/common/AppBar/AppBar";
import { Typography } from "../../components/core/Typography/Typography";
import { System } from "../../models/systems";
import { TypographyColor, TypographyVariant } from "../../constants/typography";
import { useMutation, useQuery } from "@apollo/client";
import {
  meGQL,
  updateUserGQL,
  User as UserGraphql,
} from "@onmo/graphql/graphql";
import Button from "../../components/core/Button/Button";
import { PageRoutes } from "../../constants/historyGaming";
import { Toggle } from "../../components/core/Toggle";
import { useMemo, useState } from "react";
import { NotificationErrorPopup } from "../../components/common/NotificationErrorPopup";
import { IOSInstallPopup } from "../../components/common/PopupAddHomeScreen";
import { LogoutPopup } from "../../components/common/LogoutPopup";
import { useAppDispatch, useAppSelector } from "../../components/hooks";
import { addPopupQueue } from "../../store/slices/popup";
import { PopupType } from "../../constants/popup";
import { DeleteAccountPopup } from "../../components/common/DeleteAccountPopup";
import { HelpShift } from "../../components/common/HelpShift";
import { SETTINGS_ITEM_MAP } from "../../models/settings";
import { User } from "../../models/user";
import { showSnackbar } from "../../store/slices/alert";
import { NotificationPermission } from "../../models/pushNotification";
import { useTranslation } from "react-i18next";
import { PwaDownload } from "../../components/common/PwaDownload";
import { HistoryGaming } from "../../models/historyGaming";
import { useNavigate } from "react-router-dom";
import { IThemeEnum } from "../../types/theme";

export const Settings = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { theme } = useAppSelector((state) => state.theme);
  const settingsConfig =theme?.pages?.settings || {};
  const settingsMenus = [
    {
      category: "settings_Account",
      items: settingsConfig.account || [],
    },
    {
      category: "settings_Gear up",
      items: settingsConfig.gearUp || [],
    },
    {
      category: "settings_Support",
      items: settingsConfig.support || [],
    },
  ];
  const actionItems = settingsConfig.actions || [];
  const isVerticalLayout = theme?.theme === IThemeEnum.vertical;
  const [isOpenHelpShift, setIsOpenHelpShift] = useState(false);
  const { data: dataMe } = useQuery(meGQL);
  const [updateUserMutation] = useMutation(updateUserGQL);

  const [isErrorEnableNoti, setIsErrorEnableNoti] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnableNotification, setEnableNotification] = useState(
    dataMe?.me && dataMe?.me.pushSubscription !== "null",
  );

  const getAction = (id: string) => {
    switch (id) {
      case "profile":
        return () => navigate(PageRoutes.Profile);

      case "languages":
        return () => navigate(PageRoutes.Languages);

      case "notifications":
        return isVerticalLayout
          ? () => navigate(PageRoutes.Notifications)
          : undefined;

      case "subscriptions":
        return () => navigate(PageRoutes.Subscription);

      case "friends":
        return () => navigate(PageRoutes.Friends);

      case "shop":
        return () => navigate(PageRoutes.Shop);

      case "level":
        return () => navigate(PageRoutes.Level);

      case "faq":
        return () => navigate(PageRoutes.Faq);

      case "help":
        return () => setIsOpenHelpShift(true);

      case "privacy":
        return () => navigate(PageRoutes.PrivacyPolicy);

      case "tnc":
        return () => navigate(PageRoutes.TermsAndConditions);

      case "cookie":
        return () => navigate(PageRoutes.CookiePolicy);

      default:
        return undefined;
    }
  };

  const menus = useMemo(() => {
    return settingsMenus
      .map((menu) => ({
        category: menu.category,
        value: menu.items
          .map((id: string) => {
            const item = SETTINGS_ITEM_MAP[id];
            if (!item) return null;

            return {
              ...item,
              action: getAction(id),
              isHide:
                id === "notifications" && !System.isSupportPushNotification(),
            };
          })
          .filter(Boolean),
      }))
      .filter((menu) => menu.value.length > 0);
  }, [settingsMenus]);

  const onSubmitNotification = async () => {
    setIsLoading(true);
    try {
      const pushSubscription = await User.togglePushSubscription(dataMe?.me);
      if (pushSubscription) {
        if (pushSubscription !== "null") {
          window.clevertap?.notifications.push({
            serviceWorkerPath: "/service-worker.js",
          });
        }
        await updateUserMutation({
          variables: {
            input: { pushSubscription },
          },
          update: (cache) => {
            const existingMeData = cache.readQuery<{ me: UserGraphql }>({
              query: meGQL,
            });
            // Update me Xp
            if (existingMeData?.me) {
              cache.writeQuery({
                query: meGQL,
                data: {
                  me: {
                    ...existingMeData.me,
                    pushSubscription,
                  },
                },
              });
            }
          },
        });
      }
      setEnableNotification(!isEnableNotification);
    } catch (e) {
      dispatch(showSnackbar((e as Error)?.message));
    }
    setIsLoading(false);
  };

  const onRequestPushNotification = async () => {
    if (isLoading) return;
    // Check Notification API is available then request open permission popup in your browser
    if (
      typeof Notification !== "undefined" &&
      Notification.permission !== NotificationPermission.Granted
    ) {
      const permission = await window.Notification.requestPermission();
      if (permission === NotificationPermission.Denied) {
        setEnableNotification(!isEnableNotification);
      }
    } else {
      await onSubmitNotification();
    }
  };

  const onCopy = () => {
    navigator.clipboard.writeText(dataMe?.me.id || "");
    dispatch(showSnackbar(t("snackbar_Copied")));
  };

  return (
    <div className={classes.root}>
      <AppBar
        leftItems={
          <Icon
            name="icon-bold-arrow-left-3"
            onClick={() => HistoryGaming.back(navigate)}
            data-testid="back-icon"
          />
        }
        centerItems={
          <Typography
            variant={TypographyVariant.BodyB4Medium}
            data-testid="settings-title"
          >
            {t("settings_Settings")}
          </Typography>
        }
      />
      <div className={classes.content}>
        <PwaDownload className={classes.banner} />
        {map(menus, (menuItems, index) => (
          <div key={index} className={classes.menuItem}>
            <Typography
              variant={TypographyVariant.HeadingH5Bold}
              data-testid={menuItems.category}
            >
              {t(menuItems.category)}
            </Typography>
            {map(menuItems.value, (item, index) => {
              if (item?.isHide) return null;
              return (
                <div
                  key={index}
                  className={classes.item}
                  onClick={item?.action}
                >
                  <Icon name={item?.icon} className={classes.icon} size={18} />
                  <Typography
                    variant={TypographyVariant.BodyB5Medium}
                    className={classes.title}
                    data-testid={item?.title.replace(/ /g, "-").toLowerCase()}
                  >
                    {t(item?.title || "")}
                  </Typography>
                  {item?.id === "notifications" && !isVerticalLayout ? (
                    <Toggle
                      onClick={onRequestPushNotification}
                      checked={isEnableNotification}
                    />
                  ) : (
                    <Icon name="icon-bold-arrow-right-3" size={18} />
                  )}
                </div>
              );
            })}
          </div>
        ))}
        <div>
          <Typography
            variant={TypographyVariant.BodyB5Medium}
            color={TypographyColor.Silver}
          >
            {t("settings_Version")}: {import.meta.env.VITE_BUILD_VERSION}{" "}
          </Typography>
          <div className={classes.userId}>
            <Typography
              variant={TypographyVariant.BodyB6Regular}
              color={TypographyColor.Gray}
            >
              {t("settings_User ID")}: {dataMe?.me.id}
            </Typography>
            <Icon name="icon-copy" size={14} onClick={onCopy} />
          </div>
        </div>
        <div className={classes.buttonGroup}>
          {actionItems.includes("deleteAccount") && (
            <Button
              variant="outlined"
              fullWidth
              onClick={() =>
                dispatch(
                  addPopupQueue({
                    type: PopupType.DeleteAccount,
                  }),
                )
              }
            >
              {t("settings_Delete Account")}
            </Button>
          )}
          {actionItems.includes("logout") && (
            <Button
              variant="contained"
              fullWidth
              onClick={() =>
                dispatch(
                  addPopupQueue({
                    type: PopupType.Logout,
                  }),
                )
              }
            >
              {t("settings_Logout")}
            </Button>
          )}
        </div>
      </div>
      <NotificationErrorPopup
        open={isErrorEnableNoti}
        onClose={() => setIsErrorEnableNoti(false)}
      />
      <IOSInstallPopup />
      <LogoutPopup />
      <DeleteAccountPopup />
      {isOpenHelpShift && (
        <HelpShift onClose={() => setIsOpenHelpShift(false)} />
      )}
    </div>
  );
};
